import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { transportApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import Confirm from '@/components/common/Confirm'
import StateCitySelect from '@/components/common/StateCitySelect'
import { Alert, ErrorNote, Loading, PageHeader } from '@/components/common/Ui'
import { PERMS, can, useAuth } from '@/store/auth'
import { dateTime } from '@/lib/format'

const BLANK = {
  name: '',
  contact_number: '',
  contact_number_2: '',
  contact_number_3: '',
  address: '',
  state: '',
  city: '',
  is_active: true,
}

/**
 * Add or edit one transport service, on a page of its own: its details, and
 * the routes it runs.
 *
 * A route is an ordered list of stops — Madurai → Dindigul → Trichy →
 * Chennai. A customer in any stop's city is offered the service on their
 * order page, as well as one in the service's own city. Routes are saved one
 * at a time, so a new service is saved first and the page becomes its edit
 * page with the routes section open.
 *
 * A service a customer added that is still awaiting approval shows Reject
 * and Save & approve in place of Save, as the drawer used to.
 */
export default function TransportServiceForm() {
  const { id } = useParams()
  const isNew = !id
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const editable = can(user, PERMS.deliveriesWrite)

  const [form, setForm] = useState(isNew ? BLANK : null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState(
    location.state?.justCreated ? 'Transport service added. Add the routes it runs below.' : '',
  )

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['transport-service', id],
    queryFn: () => transportApi.get(id),
    enabled: !isNew,
  })

  // Loaded once per service; a refetch after a route is saved must not throw
  // away details typed but not yet saved.
  useEffect(() => {
    if (data && (!form || form.id !== data.id)) {
      setForm({ ...data, address: data.address ?? '', state: data.state ?? '' })
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  // Taken from the saved record, so editing the form cannot flip it.
  const reviewing = Boolean(data && !data.is_approved && data.is_active)

  const save = useMutation({
    mutationFn: ({ message: _message, ...payload }) =>
      isNew ? transportApi.create(payload) : transportApi.update(id, payload),
    onSuccess: (service, variables) => {
      queryClient.invalidateQueries({ queryKey: ['transport-services'] })
      queryClient.setQueryData(['transport-service', String(service.id)], service)
      setError('')
      if (isNew) {
        navigate(`/transport-services/${service.id}`, {
          replace: true,
          state: { justCreated: true },
        })
        return
      }
      setForm({ ...service, address: service.address ?? '', state: service.state ?? '' })
      setNotice(variables.message ?? `Saved ${service.name}.`)
    },
    onError: (e) => {
      setError(errorMessage(e))
      setNotice('')
    },
  })

  if (!isNew && isLoading) return <Loading />
  if (!isNew && isError) {
    return <ErrorNote message="Could not load this transport service" onRetry={refetch} />
  }
  if (!form) return <Loading />

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  const submit = (e) => {
    e.preventDefault()
    setNotice('')
    if (!form.city) {
      setError('Choose the city this service runs from.')
      return
    }
    // Only the form's own fields go up — not the counts, tags and routes the
    // record carried in. Blank clears an extra number.
    const payload = {
      name: form.name,
      contact_number: form.contact_number,
      contact_number_2: form.contact_number_2 ?? '',
      contact_number_3: form.contact_number_3 ?? '',
      address: form.address,
      state: form.state,
      city: form.city,
      is_active: form.is_active,
    }
    if (reviewing) {
      save.mutate({
        ...payload,
        is_active: true,
        is_approved: true,
        message: `${form.name} approved — customers can now choose it.`,
      })
      return
    }
    save.mutate(payload)
  }

  return (
    <div className="transport-form-page">
      <PageHeader
        title={isNew ? 'Add transport service' : form.name || 'Transport service'}
        subtitle={
          data?.added_by_customer_name
            ? `Added by ${data.added_by_customer_name} from their order page` +
              (data.created_at ? ` · ${dateTime(data.created_at)}` : '') +
              (data.is_approved ? '' : ' — awaiting approval')
            : 'Customers are offered the active services in their city, and on its routes.'
        }
        actions={
          <Link to="/transport-services" className="btn btn-sm">
            Back to transport services
          </Link>
        }
      />

      <Alert tone="success" message={notice} onClose={() => setNotice('')} />
      <Alert tone="danger" message={error} onClose={() => setError('')} />

      <form onSubmit={submit}>
        {/* One block: the details and their Save / Cancel. */}
        <section className="panel">
        <fieldset disabled={!editable} className="plain-fieldset">
            <div className="panel-title">Details</div>
            <div className="form-grid">
              <label className="field">
                <span>Contact Name *</span>
                <input
                  value={form.name}
                  onChange={(e) => set({ name: e.target.value })}
                  placeholder="KPN Parcel Service"
                  maxLength={120}
                  required
                />
              </label>
              <label className="field">
                <span>Contact number 1 *</span>
                <input
                  type="tel"
                  inputMode="tel"
                  value={form.contact_number}
                  onChange={(e) => set({ contact_number: e.target.value })}
                  placeholder="98765 43210"
                  required
                />
              </label>
              <label className="field">
                <span>Contact number 2</span>
                <input
                  type="tel"
                  inputMode="tel"
                  value={form.contact_number_2 ?? ''}
                  onChange={(e) => set({ contact_number_2: e.target.value })}
                  placeholder="Optional"
                />
              </label>
              <label className="field">
                <span>Contact number 3</span>
                <input
                  type="tel"
                  inputMode="tel"
                  value={form.contact_number_3 ?? ''}
                  onChange={(e) => set({ contact_number_3: e.target.value })}
                  placeholder="Optional"
                />
              </label>
              <StateCitySelect
                state={form.state}
                city={form.city}
                onChange={({ state, city }) => set({ state, city })}
              />
              <label className="field span-2">
                <span>Address</span>
                <textarea
                  rows={3}
                  value={form.address ?? ''}
                  onChange={(e) => set({ address: e.target.value })}
                  placeholder="Office or booking point"
                  maxLength={500}
                />
              </label>
              {reviewing ? (
                <p className="hint span-2 review-usage">
                  {data.order_count > 0
                    ? `Already on ${data.order_count} order${data.order_count === 1 ? '' : 's'}.`
                    : 'Not on any order yet.'}{' '}
                  Check the name, number and address — correct anything here, then approve.
                  Approved services are offered to every customer.
                </p>
              ) : (
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => set({ is_active: e.target.checked })}
                  />
                  Active — offered to customers
                </label>
              )}
            </div>
        </fieldset>

        {editable && (
          <div className="form-actions panel-actions">
            {reviewing && (
              // Reject switches it off rather than deleting it: reversible,
              // and an order that already uses it keeps its record.
              <button
                type="button"
                className="btn btn-danger"
                disabled={save.isPending}
                onClick={() =>
                  save.mutate({
                    is_active: false,
                    message: `${form.name} rejected — switched off and not offered to customers.`,
                  })
                }
              >
                Reject
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              {save.isPending
                ? 'Saving…'
                : reviewing
                  ? 'Save & approve'
                  : 'Save'}
            </button>
            <Link to="/transport-services" className="btn">
              Cancel
            </Link>
          </div>
        )}
        </section>
      </form>

      {isNew ? (
        <section className="panel">
          <div className="panel-title">Routes</div>
          <p className="hint">Save the service first, then add the routes it runs.</p>
        </section>
      ) : (
        <Routes service={data} editable={editable} />
      )}
    </div>
  )
}

const newRoute = (service) => ({
  name: '',
  stops: [
    { state: service.state ?? '', city: service.city ?? '' },
    { state: service.state ?? '', city: '' },
  ],
})

function Routes({ service, editable }) {
  const queryClient = useQueryClient()
  // null: no editor open; without an id: adding; with one: editing that route.
  const [draft, setDraft] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [error, setError] = useState('')
  const routes = service.routes ?? []

  const done = {
    onSuccess: (fresh) => {
      queryClient.setQueryData(['transport-service', String(service.id)], fresh)
      queryClient.invalidateQueries({ queryKey: ['transport-services'] })
      setDraft(null)
      setRemoving(null)
      setError('')
    },
    onError: (e) => setError(errorMessage(e)),
  }
  const save = useMutation({
    mutationFn: ({ id: routeId, ...route }) =>
      routeId
        ? transportApi.updateRoute(service.id, routeId, route)
        : transportApi.addRoute(service.id, route),
    ...done,
  })
  const remove = useMutation({
    mutationFn: (routeId) => transportApi.removeRoute(service.id, routeId),
    ...done,
  })

  const setStop = (index, patch) =>
    setDraft((d) => ({
      ...d,
      stops: d.stops.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }))
  // A stop on the way goes in just before the destination.
  const addStop = () =>
    setDraft((d) => {
      const stops = [...d.stops]
      stops.splice(stops.length - 1, 0, { state: stops.at(-2)?.state ?? '', city: '' })
      return { ...d, stops }
    })
  const removeStop = (index) =>
    setDraft((d) => ({ ...d, stops: d.stops.filter((_, i) => i !== index) }))
  // Among the stops on the way only: From and To keep their places.
  const moveStop = (index, by) =>
    setDraft((d) => {
      const stops = [...d.stops]
      const [stop] = stops.splice(index, 1)
      stops.splice(index + by, 0, stop)
      return { ...d, stops }
    })

  const submit = (e) => {
    e.preventDefault()
    const stops = draft.stops.filter((s) => s.city)
    if (stops.length < 2) {
      setError('A route needs at least two stops, each with a city.')
      return
    }
    save.mutate({
      id: draft.id,
      name: draft.name || null,
      stops: stops.map((s) => ({ city: s.city, state: s.state || null })),
    })
  }

  return (
    <section className="panel">
      <div className="panel-title panel-title-row">
        Routes
        {editable && !draft && (
          <button type="button" className="btn btn-sm" onClick={() => setDraft(newRoute(service))}>
            Add route
          </button>
        )}
      </div>
      <p className="hint">
        The lines this service runs, stop by stop. Customers in any stop’s city are offered it, as
        well as customers in {service.city}.
      </p>
      <Alert tone="danger" message={error} onClose={() => setError('')} />

      {routes.length === 0 && !draft && <p className="muted">No routes yet.</p>}

      <ul className="route-list">
        {routes.map((route, index) => (
          <li key={route.id} className="route-card">
            <div className="route-card-head">
              <strong>{route.name || `Route ${index + 1}`}</strong>
              <span className="muted">{route.stops.length} stops</span>
            </div>
            <ol className="route-stops">
              {route.stops.map((stop, i) => (
                <li key={`${stop.city}-${i}`}>
                  {stop.city}
                  {stop.state && <span className="muted"> · {stop.state}</span>}
                </li>
              ))}
            </ol>
            {editable && (
              <div className="route-card-actions">
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() =>
                    setDraft({
                      id: route.id,
                      name: route.name ?? '',
                      stops: route.stops.map((s) => ({ city: s.city, state: s.state ?? '' })),
                    })
                  }
                >
                  Edit
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRemoving(route)}>
                  Delete
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {draft && (
        <form className="route-form" onSubmit={submit}>
          <div className="panel-title">{draft.id ? 'Edit route' : 'New route'}</div>
          <label className="field">
            <span>Route name (optional)</span>
            <input
              value={draft.name}
              maxLength={120}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Chennai line"
            />
          </label>

          {/* Built the way a route is said: from here, through these, to
              there. The line above the rows reads it back as it is built. */}
          <p className="route-preview">
            {draft.stops.map((s) => s.city || '…').join('  →  ')}
          </p>

          <div className="route-stop-head" aria-hidden="true">
            <span />
            <span>State</span>
            <span>City</span>
            <span />
          </div>
          <ol className="route-stop-editor">
            {draft.stops.map((stop, index) => {
              const first = index === 0
              const last = index === draft.stops.length - 1
              return (
                <li key={index} className="route-stop-row">
                  <span className={`route-stop-tag${first || last ? ' ends' : ''}`}>
                    {first ? 'From' : last ? 'To' : `Stop ${index}`}
                  </span>
                  <div className="route-stop-fields">
                    <StateCitySelect
                      state={stop.state}
                      city={stop.city}
                      onChange={({ state, city }) => setStop(index, { state, city })}
                    />
                  </div>
                  {first || last ? (
                    <span />
                  ) : (
                    <div className="route-stop-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-icon"
                        aria-label={`Move stop ${index} up`}
                        disabled={index === 1}
                        onClick={() => moveStop(index, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-icon"
                        aria-label={`Move stop ${index} down`}
                        disabled={index === draft.stops.length - 2}
                        onClick={() => moveStop(index, 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-icon"
                        aria-label={`Remove stop ${index}`}
                        onClick={() => removeStop(index)}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  {/* Between the last stop on the way and the destination. */}
                  {index === draft.stops.length - 2 && (
                    <button type="button" className="btn-link route-add-stop" onClick={addStop}>
                      + Add stop on the way
                    </button>
                  )}
                </li>
              )
            })}
          </ol>

          <div className="route-form-actions">
            <button type="button" className="btn" onClick={() => setDraft(null)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save route'}
            </button>
          </div>
        </form>
      )}

      <Confirm
        open={Boolean(removing)}
        title="Delete this route?"
        message={
          removing
            ? `${removing.name || removing.summary} will be removed. Customers on it will no longer ` +
              'be offered this service unless they are in its own city or on another route.'
            : ''
        }
        busy={remove.isPending}
        onConfirm={() => remove.mutate(removing.id)}
        onCancel={() => setRemoving(null)}
      />
    </section>
  )
}
